import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { RefreshCw, Plus, ExternalLink, Check, Filter, Search, WifiOff, ArrowUpRight } from 'lucide-react';
import toast from 'react-hot-toast';

interface NewsItem {
  title: string;
  link: string;
  pubDate: string;
  description: string;
  source: string;
  category: string;
}

interface NewsFeedProps {
  onSelectNews: (item: NewsItem) => void;
}

const NewsFeed: React.FC<NewsFeedProps> = ({ onSelectNews }) => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSource, setSelectedSource] = useState('all');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [lastClickedItem, setLastClickedItem] = useState<string | null>(null);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 3000;

  const checkOnline = () => {
    return typeof navigator !== 'undefined' && navigator.onLine;
  };

  const fetchNews = async (retry = false) => {
    if (!checkOnline()) {
      setError('Unable to load news feed - please check your internet connection');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-rss`,
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch news: HTTP status ${response.status}`);
      }

      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.message || 'Failed to load news feed');
      }

      if (!data.items || !Array.isArray(data.items)) {
        throw new Error('Invalid feed data format');
      }

      setNews(data.items);
      setRetryCount(0);
    } catch (error) {
      console.error('Error fetching news:', error);
      
      if (retry && retryCount < MAX_RETRIES) {
        setRetryCount(prev => prev + 1);
        setTimeout(() => {
          fetchNews(true);
        }, RETRY_DELAY);
        
        toast.error(`Failed to load news feed - retrying (${retryCount + 1}/${MAX_RETRIES})`);
      } else {
        setError(error instanceof Error ? error.message : 'Failed to load news feed');
        toast.error('Failed to load news feed');
        setNews([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews(true);

    const handleOnline = () => {
      setError(null);
      fetchNews(true);
    };

    const handleOffline = () => {
      setError('Unable to load news feed - please check your internet connection');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleSelectItem = (item: NewsItem) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(item.link)) {
      newSelected.delete(item.link);
    } else {
      newSelected.add(item.link);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredNews.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredNews.map(item => item.link)));
    }
  };

  const handleUseSelected = () => {
    const selectedNews = filteredNews.filter(item => selectedItems.has(item.link));
    selectedNews.forEach(item => {
      onSelectNews(item);
    });
    setSelectedItems(new Set());
    toast.success(`${selectedNews.length} stories added to post`);
  };

  const handleNewsClick = (item: NewsItem) => {
    setLastClickedItem(item.link);
    onSelectNews(item);
    toast.success('Story added to post editor');

    // Reset the highlight after 2 seconds
    setTimeout(() => {
      setLastClickedItem(null);
    }, 2000);
  };

  // Filter news items
  const filteredNews = news.filter(item => {
    const matchesSearch = searchTerm === '' || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSource = selectedSource === 'all' || item.source === selectedSource;

    return matchesSearch && matchesCategory && matchesSource;
  });

  if (loading) {
    return (
      <div className="animate-pulse bg-white rounded-lg shadow-sm p-6 space-y-4">
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-8">
          <WifiOff className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => fetchNews(true)}
            className="btn btn-primary"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Latest News</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchNews(true)}
            className="text-primary hover:text-primary-dark"
            title="Refresh news"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search news stories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full border rounded-lg py-2 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pl-10 w-full border rounded-lg py-2 pr-3 appearance-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {Array.from(new Set(news.map(item => item.category))).map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              className="pl-10 w-full border rounded-lg py-2 pr-3 appearance-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All Sources</option>
              {Array.from(new Set(news.map(item => item.source))).map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2 flex justify-end gap-4">
            <button
              onClick={handleSelectAll}
              className="btn btn-outline"
            >
              {selectedItems.size === filteredNews.length ? 'Deselect All' : 'Select All'}
            </button>
            {selectedItems.size > 0 && (
              <button
                onClick={handleUseSelected}
                className="btn btn-primary"
              >
                Use Selected ({selectedItems.size})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* News Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredNews.map((item, index) => (
          <div
            key={index}
            className={`relative border rounded-lg p-4 transition-all cursor-pointer group ${
              selectedItems.has(item.link)
                ? 'border-primary bg-primary/5'
                : lastClickedItem === item.link
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-primary hover:shadow-md'
            }`}
            onClick={() => handleNewsClick(item)}
          >
            <div className="absolute top-4 right-4 flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectItem(item);
                }}
                className={`p-1 rounded-full transition-colors ${
                  selectedItems.has(item.link)
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                }`}
              >
                <Check className="h-4 w-4" />
              </button>
              <a
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1 rounded-full bg-gray-100 text-gray-400 hover:bg-gray-200"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>

            <div className="mb-2">
              <h3 className="text-lg font-medium text-gray-900 group-hover:text-primary line-clamp-2 pr-16">
                {item.title}
                <ArrowUpRight className="inline-block h-4 w-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
              </h3>
            </div>

            <p className="text-gray-600 text-sm mb-3 line-clamp-3">
              {item.description}
            </p>

            <div className="flex items-center text-sm text-gray-500">
              <span className="font-medium text-gray-700">{item.source}</span>
              <span className="mx-2">•</span>
              <span>{format(new Date(item.pubDate), 'MMM d, yyyy')}</span>
              {item.category && (
                <>
                  <span className="mx-2">•</span>
                  <span className="text-primary">{item.category}</span>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredNews.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          No news stories found matching your filters
        </div>
      )}
    </div>
  );
};

export default NewsFeed;